import { ProposalJobHandoff } from "./ProposalJobHandoff";
/**
 * ProposalDetail - View a proposal with all its details
 */

import { formatCalendarDate, isCalendarDatePast } from '../../lib/calendarDate';
import React, { useState } from 'react';
import ProposalWorkflow, { type ProposalWorkflowData } from './ProposalWorkflow';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Chip,
  Stack,
  Divider,
  CircularProgress,
  Alert,
  Dropdown,
  Menu,
  MenuButton,
  MenuItem,
} from '@mui/joy';
import {
  ArrowLeft as ArrowBack,
  CaretDown as ArrowDropDown,
  PencilSimple as Edit,
  PaperPlaneTilt as Send,
  Copy as ContentCopy,
  Trash as Delete,
  CheckCircle,
  EnvelopeOpen as Drafts,
  EnvelopeOpen as MarkEmailRead,
  XCircle as Cancel,
  Hourglass as HourglassEmpty,
  User as Person,
  House as Home,
  Calendar as CalendarToday,
} from '@phosphor-icons/react';
import { proposalsApi } from '../../lib/proposalsApi';
import { api } from '../../lib/api';
import { ResponsiveTable } from '../../components/common/ResponsiveTable';
import type { ProposalStatus, TierType } from '../../types/proposals';

const statusConfig: Record<ProposalStatus, { label: string; color: 'neutral' | 'primary' | 'success' | 'warning' | 'danger'; icon: React.ReactNode }> = {
  draft: { label: 'Draft', color: 'neutral', icon: <Drafts /> },
  sent: { label: 'Sent', color: 'primary', icon: <Send /> },
  viewed: { label: 'Viewed', color: 'warning', icon: <MarkEmailRead /> },
  accepted: { label: 'Accepted', color: 'success', icon: <CheckCircle /> },
  declined: { label: 'Declined', color: 'danger', icon: <Cancel /> },
  expired: { label: 'Expired', color: 'neutral', icon: <HourglassEmpty /> },
};

const tierColors: Record<TierType, 'neutral' | 'primary' | 'success'> = {
  good: 'neutral',
  better: 'primary',
  best: 'success',
};

export default function ProposalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const [reviewOpen, setReviewOpen] = useState(searchParams.get('review') === '1');

  const { data: proposal, isLoading, error } = useQuery({
    queryKey: ['proposal', id, searchParams.get('handoff')],
    queryFn: () => proposalsApi.get(parseInt(id!)),
  });

  const workflow = useQuery({
    queryKey: ['proposal-workflow', proposal?.id, proposal?.updated_at],
    enabled: !!proposal,
    queryFn: async () =>
      (await api.get<ProposalWorkflowData>(`/api/proposals/${proposal!.id}/workflow/`)).data,
    refetchInterval: (query) =>
      ['pending', 'processing'].includes(query.state.data?.acceptance.state || '')
        ? 5000
        : query.state.data?.acceptance.state === 'error' ? 30000 : false,
  });

  const markAsSentMutation = useMutation({
    mutationFn: () => proposalsApi.markAsSent(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposal', id] });
      toast.success('Proposal marked as sent');
    },
    onError: () => {
      toast.error('Failed to mark proposal as sent');
    },
  });

  const duplicateMutation = useMutation({
    mutationFn: () => proposalsApi.duplicate(parseInt(id!)),
    onSuccess: (newProposal) => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      toast.success('Proposal duplicated');
      navigate(`/crm/proposals/${newProposal.id}/edit`);
    },
    onError: () => {
      toast.error('Failed to duplicate proposal');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => proposalsApi.delete(parseInt(id!)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['proposals'] });
      toast.success('Proposal deleted');
      navigate('/crm/proposals');
    },
    onError: (error: Error & { responseData?: { error?: string; detail?: string } }) => {
      toast.error(error.responseData?.error || error.responseData?.detail || 'Failed to delete proposal');
    },
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !proposal) {
    return (
      <Alert color="danger" sx={{ m: 3 }}>
        Error loading proposal. Please try again.
      </Alert>
    );
  }

  const statusInfo = statusConfig[proposal.status];

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ mb: 3, justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' } }}
      >
        <Box>
          <Button
            variant="plain"
            size="sm"
            startDecorator={<ArrowBack />}
            onClick={() => navigate('/crm/proposals')}
            sx={{ mb: 1 }}
          >
            Back to Proposals
          </Button>
          <Typography level="h3">{proposal.proposal_number}</Typography>
          <Typography level="title-lg" sx={{ color: 'text.secondary' }}>
            {proposal.title}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={<ContentCopy />}
            onClick={() => duplicateMutation.mutate()}
            loading={duplicateMutation.isPending}
          >
            Duplicate
          </Button>
          <Button
            variant="outlined"
            size="sm"
            color="primary"
            startDecorator={<Edit />}
            component={Link}
            disabled={!!proposal.acceptance_date || proposal.status === 'accepted'}
            to={`/crm/proposals/${id}/edit`}
          >
            Edit
          </Button>
          {['draft', 'sent', 'viewed', 'expired'].includes(proposal.status) && (
            <Dropdown>
              <MenuButton
                slots={{ root: Button }}
                slotProps={{
                  root: {
                    variant: 'solid',
                    color: 'success',
                    size: 'sm',
                    startDecorator: <Send />,
                    endDecorator: <ArrowDropDown />,
                    loading: markAsSentMutation.isPending,
                  } as React.ComponentProps<typeof Button>,
                }}
              >
                {proposal.status === 'draft' ? 'Send' : 'Resend'}
              </MenuButton>
              <Menu placement="bottom-end">
                <MenuItem onClick={() => setReviewOpen(true)}>
                  <Send style={{ marginRight: 8 }} size={18} />
                  {proposal.status === 'draft' ? 'Email Proposal' : 'Resend Email'}
                </MenuItem>
                {proposal.status === 'draft' && (
                  <MenuItem onClick={() => markAsSentMutation.mutate()}>
                    <CheckCircle style={{ marginRight: 8 }} size={18} />
                    Mark as Sent
                  </MenuItem>
                )}
              </Menu>
            </Dropdown>
          )}
        </Stack>
      </Stack>

      <ProposalWorkflow key={id} proposal={proposal} query={workflow} reviewOpen={reviewOpen} onCloseReview={() => setReviewOpen(false)} />

      {/* Status and Info Cards */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <Card variant="outlined" sx={{ flex: 1, minWidth: { xs: 140, sm: 200 } }}>
          <CardContent>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
              Status
            </Typography>
            <Chip
              size="lg"
              color={statusInfo.color}
              variant="soft"
              startDecorator={statusInfo.icon}
            >
              {statusInfo.label}
            </Chip>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, minWidth: { xs: 140, sm: 200 } }}>
          <CardContent>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
              <Person style={{ marginRight: 4 }} />
              Contact
            </Typography>
            <Typography level="title-md">{proposal.contact_name || '—'}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, minWidth: { xs: 140, sm: 200 } }}>
          <CardContent>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
              <Home style={{ marginRight: 4 }} />
              Opportunity
            </Typography>
            <Typography level="title-md">{proposal.opportunity_name || '—'}</Typography>
          </CardContent>
        </Card>

        <Card variant="outlined" sx={{ flex: 1, minWidth: { xs: 140, sm: 200 } }}>
          <CardContent>
            <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 1 }}>
              <CalendarToday style={{ marginRight: 4 }} />
              Valid Until
            </Typography>
            <Typography
              level="title-md"
              color={isCalendarDatePast(proposal.valid_until) ? 'danger' : 'neutral'}
            >
              {formatCalendarDate(proposal.valid_until)}
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {proposal.status === 'accepted' && <Box sx={{ mb: 3 }}><ProposalJobHandoff endpoint={`/api/proposals/${proposal.id}/job-handoff/`} materialsProposalId={proposal.service_configuration?.version ? proposal.id : undefined} /></Box>}
      {/* Acceptance Info (if accepted) */}
      {proposal.status === 'accepted' && proposal.accepted_tier && (
        <Alert color="success" sx={{ mb: 3 }} startDecorator={<CheckCircle />}>
          <Box>
            <Typography level="title-md">Proposal Accepted!</Typography>
            <Typography level="body-sm">
              {proposal.acceptance_name} accepted the{' '}
              <strong>{proposal.tiers.find((t) => t.id === proposal.accepted_tier)?.name}</strong> tier
              on {proposal.acceptance_date && new Date(proposal.acceptance_date).toLocaleDateString()}
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Pricing Tiers */}
      <Typography level="title-lg" sx={{ mb: 2 }}>
        Pricing Tiers
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
        {[...proposal.tiers]
          .sort((a, b) => a.sort_order - b.sort_order)
          .map((tier) => (
            <Card
              key={tier.id}
              variant={proposal.accepted_tier === tier.id ? 'soft' : 'outlined'}
              color={proposal.accepted_tier === tier.id ? 'success' : tierColors[tier.tier_type]}
              sx={{
                flex: 1,
                minWidth: 280,
                maxWidth: 400,
                position: 'relative',
              }}
            >
              {tier.is_recommended && proposal.accepted_tier !== tier.id && (
                <Chip
                  size="sm"
                  color="warning"
                  variant="solid"
                  sx={{ position: 'absolute', top: -10, right: 10 }}
                >
                  Recommended
                </Chip>
              )}
              {proposal.accepted_tier === tier.id && (
                <Chip
                  size="sm"
                  color="success"
                  variant="solid"
                  startDecorator={<CheckCircle />}
                  sx={{ position: 'absolute', top: -10, right: 10 }}
                >
                  Selected
                </Chip>
              )}
              <CardContent>
                <Typography level="title-sm" sx={{ textTransform: 'uppercase', mb: 0.5 }}>
                  {tier.tier_type_label || tier.tier_type}
                </Typography>
                <Typography level="h4" sx={{ mb: 1 }}>
                  {tier.name}
                </Typography>
                {tier.description && (
                  <Typography level="body-sm" sx={{ mb: 2 }}>
                    {tier.description}
                  </Typography>
                )}

                {/* Advantages */}
                {tier.advantages && tier.advantages.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {tier.advantages.map((adv, i) => (
                      <Typography key={i} level="body-xs" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <CheckCircle color="var(--joy-palette-success-500)" />
                        {adv}
                      </Typography>
                    ))}
                  </Box>
                )}

                {/* Line Items */}
                {tier.line_items && tier.line_items.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    {tier.line_items.map((item) => (
                      <Typography key={item.id} level="body-xs" sx={{ color: 'text.secondary' }}>
                        • {item.description}
                      </Typography>
                    ))}
                  </Box>
                )}

                <Divider sx={{ my: 2 }} />

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography level="body-sm">Subtotal:</Typography>
                  <Typography level="body-sm">{formatCurrency(tier.subtotal)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography level="body-sm">Tax ({tier.tax_rate}%):</Typography>
                  <Typography level="body-sm">{formatCurrency(tier.tax_amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography level="title-md">Total:</Typography>
                  <Typography level="title-md" fontWeight="lg">
                    {formatCurrency(tier.total_amount)}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ))}
      </Box>

      {/* Add-ons & Fees */}
      {proposal.optional_upgrades && proposal.optional_upgrades.length > 0 && (
        <Box sx={{ mb: 4 }}>
          <Typography level="title-lg" sx={{ mb: 2 }}>
            Add-ons & Fees
          </Typography>
          <ResponsiveTable<(typeof proposal.optional_upgrades)[number]>
            rows={proposal.optional_upgrades}
            getRowKey={(upgrade) => upgrade.id}
            columns={[
              {
                key: 'name',
                sortValue: (row) => row.name,
                header: 'Name',
                mobileVariant: 'header',
                render: (upgrade) => (
                  <Stack direction="row" spacing={1} alignItems="center">
                    {upgrade.is_selected && (
                      <Chip size="sm" color="success" variant="soft" startDecorator={<CheckCircle size={16} />}>
                        Selected
                      </Chip>
                    )}
                    <Typography level="title-sm">{upgrade.name}</Typography>
                  </Stack>
                ),
              },
              {
                key: 'selected',
                sortValue: (row) => row.is_selected,
                header: 'Selected',
                hideOnMobile: true,
                render: (upgrade) =>
                  upgrade.is_selected ? (
                    <Chip size="sm" color="success" variant="soft">
                      <CheckCircle size={18} />
                    </Chip>
                  ) : (
                    '—'
                  ),
              },
              {
                key: 'description',
                sortValue: (row) => row.description,
                header: 'Description',
                mobileVariant: 'body',
                render: (upgrade) => (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    {upgrade.description || '—'}
                  </Typography>
                ),
              },
              {
                key: 'quantity',
                sortValue: (row) => Number(row.quantity),
                header: 'Qty',
                render: (upgrade) => <Typography level="body-sm">{upgrade.quantity}</Typography>,
              },
              {
                key: 'unit_price',
                sortValue: (row) => Number(row.unit_price),
                header: 'Unit Price',
                render: (upgrade) => (
                  <Typography level="body-sm">{formatCurrency(upgrade.unit_price)}</Typography>
                ),
              },
              {
                key: 'total',
                header: 'Total',
                render: (upgrade) => (
                  <Typography level="body-sm" fontWeight={upgrade.is_selected ? 'lg' : 'md'}>
                    {formatCurrency(upgrade.total_price)}
                  </Typography>
                ),
              },
            ]}
          />
        </Box>
      )}

      {/* Timeline */}
      <Typography id="proposal-activity-timeline" level="title-lg" sx={{ mb: 2 }}>
        Activity Timeline
      </Typography>
      <Card component="section" aria-labelledby="proposal-activity-timeline" variant="outlined" sx={{ mb: 4 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip size="sm" variant="soft">
                Created
              </Chip>
              <Typography level="body-sm">
                {new Date(proposal.created_at).toLocaleString()}
              </Typography>
              {proposal.created_by_name && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  by {proposal.created_by_name}
                </Typography>
              )}
            </Box>

            {proposal.sent_at && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip size="sm" variant="soft" color="primary">
                  Sent
                </Chip>
                <Typography level="body-sm">
                  {new Date(proposal.sent_at).toLocaleString()}
                </Typography>
              </Box>
            )}

            {proposal.viewed_at && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip size="sm" variant="soft" color="warning">
                  Viewed
                </Chip>
                <Typography level="body-sm">
                  {new Date(proposal.viewed_at).toLocaleString()}
                </Typography>
              </Box>
            )}

            {proposal.acceptance_date && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Chip size="sm" variant="soft" color="success">
                  Accepted
                </Chip>
                <Typography level="body-sm">
                  {new Date(proposal.acceptance_date).toLocaleString()}
                </Typography>
                {proposal.acceptance_name && (
                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                    by {proposal.acceptance_name}
                  </Typography>
                )}
              </Box>
            )}
            <Divider />
            <Typography level="title-md">Activity · latest 100 events</Typography>
            {workflow.isPending && (
              <Typography level="body-sm">Loading proposal activity…</Typography>
            )}
            {workflow.isError && (
              <Alert
                color="warning"
                endDecorator={<Button onClick={() => workflow.refetch()}>Retry</Button>}
              >
                Proposal activity is unavailable.
              </Alert>
            )}
            {workflow.isSuccess && !workflow.data.events.length && (
              <Typography level="body-sm">
                New sends, customer views, acceptance, and document events will appear here.
              </Typography>
            )}
            {workflow.isSuccess && workflow.data.events.map((event) => (
              <Box key={event.id}>
                <Typography level="body-sm">{event.description}</Typography>
                <Typography level="body-xs">
                  {event.actor} · {new Date(event.created_at).toLocaleString()}
                </Typography>
              </Box>
            ))}
          </Stack>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      {!proposal.acceptance_date && proposal.status !== 'accepted' && <Card variant="outlined" color="danger" sx={{ mt: 4 }}>
        <CardContent>
          <Typography level="title-md" color="danger" sx={{ mb: 1 }}>
            Danger Zone
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
            Once you delete a proposal, there is no going back.
          </Typography>
          <Button
            variant="outlined"
            color="danger"
            size="sm"
            startDecorator={<Delete />}
            onClick={() => {
              if (confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) {
                deleteMutation.mutate();
              }
            }}
            loading={deleteMutation.isPending}
          >
            Delete Proposal
          </Button>
        </CardContent>
      </Card>}
    </Box>
  );
}
